import React, { FC } from 'react';
import styled from '@emotion/styled';
import { useTheme } from '@emotion/react';
import { shallow } from 'zustand/shallow';
import { useAppEvents, useTranslation } from '$hooks';
import { IBasketLine, IPriceCustomerSpecificPrice, IShippingType } from '$models';
import { formatPrice, isSpecialPrice, mq } from '$lib/helpers';
import { B2CDeliveryMethod } from '$constants/delivery';
import { useAppStore, useBasket } from '~/store';
import { Flex } from '$components/layouts';
import { AppContentContainer, AppLinkHandler } from '~/modules/app';
import { Splash } from '$components/elements/product-image/components/splash';
import { Text } from '$components/elements/text';
import { Image } from '$components/elements/image';
import { Counter } from '$components/elements/counter';
import { Button } from '$components/elements/button';

type BasketItemProps = {
    line: IBasketLine;
    shippingType?: IShippingType;
    onEditCardClicked?: () => void;
};

export const BasketItem: FC<BasketItemProps> = ({
    line,
    shippingType,
    onEditCardClicked,
}: BasketItemProps): JSX.Element => {
    const { translate } = useTranslation();
    const { removeBasketLine, updateBasketLine, updatingBasket, updateGift } = useBasket();
    const { appOnProductClick, appAddToFavorite, appRemoveFromFavorite } = useAppEvents();
    const { isApp, favoriteIds, addFavoriteId, removeFavoriteId, isAnonymous } = useAppStore(
        (state) => ({
            isApp: state.isApp,
            favoriteIds: state.favoriteIds,
            addFavoriteId: state.addFavoriteId,
            removeFavoriteId: state.removeFavoriteId,
            isAnonymous: state.isAnonymous,
        }),
        shallow
    );
    const theme = useTheme();

    const isShopOnly =
        line.b2CDeliveryMethod === B2CDeliveryMethod.ShopOnly ||
        line.b2CDeliveryMethod === B2CDeliveryMethod.PreorderShopOnly;
    const isDirectOnly =
        line.b2CDeliveryMethod === B2CDeliveryMethod.DirectOnly ||
        line.b2CDeliveryMethod === B2CDeliveryMethod.PreorderDirectOnly;
    const isDownloadable = line.b2CDeliveryMethod === B2CDeliveryMethod.Download;

    const onRemoveLine = (line: IBasketLine) => {
        if (line.isGiftShopItem) {
            updateGift({
                isInternalItem: false,
                description: undefined,
                quantity: 0,
                itemNumber: line.product?.itemNumber,
            });
        } else {
            removeBasketLine(line?.product?.itemNumber);
        }
    };

    const onUpdateLine = (line: IBasketLine, quantity: number) => {
        if (line.isGiftShopItem) {
            updateGift({
                isInternalItem: false,
                description: undefined,
                quantity: quantity,
                itemNumber: line.product?.itemNumber,
            });
        } else {
            updateBasketLine(line?.product?.itemNumber, quantity);
        }
    };

    const addToFavorite = (id?: string) => {
        if (id) {
            addFavoriteId(id);
            appAddToFavorite(id);
        }
    };

    const removeFromFavorite = (id?: string) => {
        if (id) {
            removeFavoriteId(id);
            appRemoveFromFavorite(id);
        }
    };

    const hasSpecialPrice = (prices: IPriceCustomerSpecificPrice[]) => {
        return prices.some((price) => isSpecialPrice(price.accountRelation));
    };

    const renderPriceSection = (isDiscount?: boolean, price?: number, quantity?: number) => (
        <MutedText discount={isDiscount} alignRight>
            {`${formatPrice(price)}${(quantity || 0) > 1 ? ` ${translate('productItem.productRowItem.unit')}` : ''}`}
        </MutedText>
    );

    return (
        <BasketItemWrapper justifyContent="space-between" column isApp={isApp}>
            <Flex>
                <AppLinkHandler
                    href={line.productUrl}
                    appClickEvent={() => appOnProductClick(line.product?.itemNumber)}
                >
                    <LineImageWrapper>
                        {!!line.primaryImageUrl && (
                            <>
                                <Image height={80} src={line.primaryImageUrl} />
                                {!!line.product?.itemPriceTagUrl && (
                                    <SplashContainer>
                                        <Splash src={line.product.itemPriceTagUrl} />
                                    </SplashContainer>
                                )}
                            </>
                        )}
                    </LineImageWrapper>
                </AppLinkHandler>
                <LineDescription column grow>
                    {line.description ? (
                        <>
                            <AppLinkHandler
                                href={line.productUrl}
                                appClickEvent={() => appOnProductClick(line.product?.itemNumber)}
                            >
                                <LineTitle as="h2" variant="display6">
                                    {line.product?.itemName}
                                </LineTitle>
                            </AppLinkHandler>
                            <DescriptionText>
                                {translate('productItem.productRowItem.giftProductLabel')}
                            </DescriptionText>
                        </>
                    ) : (
                        <>
                            <AppLinkHandler
                                href={line.productUrl}
                                appClickEvent={() => appOnProductClick(line.product?.itemNumber)}
                            >
                                <LineTitle as="h2" variant="display6">
                                    {line.product?.itemName}
                                </LineTitle>
                            </AppLinkHandler>
                            {!isDownloadable && !!line.product?.itemGroupName && (
                                <DescriptionText>{line.product?.itemGroupName}</DescriptionText>
                            )}
                        </>
                    )}
                    {shippingType && !isDownloadable && (
                        <MutedText>{`${translate('productItem.productRowItem.deliveryTimePrefixLabel')} ${
                            line.deliveryTime
                        }`}</MutedText>
                    )}
                    {isDownloadable && (
                        <MutedText>{`${translate('productItem.productRowItem.deliveryTimePrefixLabel')} ${
                            line.downloadDeliveryText
                        }`}</MutedText>
                    )}
                    {!!line.description && <MutedText>{line.description}</MutedText>}
                    {isDirectOnly && (
                        <AlertText>{translate('productItem.productRowItem.directOnlyWarningMessage')}</AlertText>
                    )}
                    {isShopOnly && !line.isGiftShopItem && (
                        <AlertText>{translate('productItem.productRowItem.shopOnlyWarningMessage')}</AlertText>
                    )}
                    {!!line?.voucherDiscount && (
                        <MutedText>
                            {translate('productItem.productRowItem.voucherLabel')}
                            <span>{line.voucher}</span>
                        </MutedText>
                    )}
                    {!!line?.mixAndMatchCampaignDiscount && !!line.mixAndMatchCampaignName && (
                        <Text size={theme.fontSizes.xs}>
                            {translate('productItem.productRowItem.mixMatchLabel')}
                            <span>{line.mixAndMatchCampaignName}</span>
                        </Text>
                    )}
                    {line.hasErrors && <AlertText>{line.errorMessage}</AlertText>}
                    {/* Basket content mobile */}
                    <AppContentContainer>
                        <MobileContent column>
                            {!line.description && (
                                <CounterContainer>
                                    <Counter
                                        initial={line.quantity}
                                        onUpdate={(quantity) => onUpdateLine(line, quantity)}
                                        isDisabled={updatingBasket}
                                    />
                                </CounterContainer>
                            )}
                            <Flex column>
                                {!!line?.description && (
                                    <RemoveItem onClick={onEditCardClicked} variant="link">
                                        {translate('checkout.giftDetails.giftEditCardLabel')}
                                    </RemoveItem>
                                )}
                                <Flex justifyContent="space-between" alignItems="center">
                                    <RemoveItem onClick={() => onRemoveLine(line)} variant="link">
                                        {translate('productItem.productRowItem.removeProduct')}
                                    </RemoveItem>
                                    <PriceContainer>
                                        {!!line.prices?.length && hasSpecialPrice(line.prices) ? (
                                            <>
                                                <SpecialPrice>{formatPrice(line.lineTotalPrice)}</SpecialPrice>
                                                {(line?.quantity || 0) > 1 &&
                                                    renderPriceSection(false, line.lineSinglePrice, line.quantity)}
                                            </>
                                        ) : (
                                            <>
                                                <Price>{formatPrice(line.lineTotalPrice)}</Price>
                                                {!!line?.lineDiscount &&
                                                    renderPriceSection(
                                                        true,
                                                        line.lineSinglePriceExDiscount,
                                                        line.quantity
                                                    )}
                                                {(line?.quantity || 0) > 1 &&
                                                    renderPriceSection(false, line.lineSinglePrice, line.quantity)}
                                            </>
                                        )}
                                    </PriceContainer>
                                </Flex>
                            </Flex>
                        </MobileContent>
                    </AppContentContainer>
                    {/* Basket content for app */}
                    <AppContentContainer onlyShowInApp>
                        <AppBasketContent flex column>
                            <Counter
                                initial={line.quantity}
                                onUpdate={(quantity) => onUpdateLine(line, quantity)}
                                isDisabled={updatingBasket}
                                appStyle
                                disableInfoMessage
                            />
                            <Flex>
                                <AppCta onClick={() => onRemoveLine(line)} variant="link">
                                    {translate('productItem.productRowItem.removeProduct')}
                                </AppCta>
                                {/* Line description means it's a gift card for gift product */}
                                {!line?.description && !isAnonymous && (
                                    <>
                                        {favoriteIds?.some((id) => id === line?.product?.itemNumber) ? (
                                            <AppCta
                                                onClick={() => removeFromFavorite(line?.product?.itemNumber)}
                                                variant="link"
                                            >
                                                {translate('app.basket.removeFromFavorite')}
                                            </AppCta>
                                        ) : (
                                            <AppCta
                                                onClick={() => addToFavorite(line?.product?.itemNumber)}
                                                variant="link"
                                            >
                                                {translate('app.basket.addToFavorite')}
                                            </AppCta>
                                        )}
                                    </>
                                )}
                            </Flex>
                            <AppPriceContainer>
                                {!!line.prices?.length && hasSpecialPrice(line.prices) ? (
                                    <>
                                        <SpecialPrice>{formatPrice(line.lineTotalPrice)}</SpecialPrice>
                                        {(line?.quantity || 0) > 1 &&
                                            renderPriceSection(false, line.lineSinglePrice, line.quantity)}
                                    </>
                                ) : (
                                    <>
                                        <Price>{formatPrice(line.lineTotalPrice)}</Price>
                                        {!!line?.lineDiscount &&
                                            renderPriceSection(true, line.lineSinglePriceExDiscount, line.quantity)}
                                        {(line?.quantity || 0) > 1 &&
                                            renderPriceSection(false, line.lineSinglePrice, line.quantity)}
                                    </>
                                )}
                            </AppPriceContainer>
                        </AppBasketContent>
                    </AppContentContainer>
                </LineDescription>
            </Flex>
            {/* Basket content desktop */}
            <AppContentContainer>
                <DesktopContent column alignItems="flex-end">
                    <PriceContainer>
                        {!!line.prices?.length && hasSpecialPrice(line.prices) ? (
                            <>
                                <SpecialPrice>{formatPrice(line.lineTotalPrice)}</SpecialPrice>
                                {(line?.quantity || 0) > 1 &&
                                    renderPriceSection(false, line.lineSinglePrice, line.quantity)}
                            </>
                        ) : (
                            <>
                                <Price>{formatPrice(line.lineTotalPrice)}</Price>
                                {!!line?.lineDiscount &&
                                    renderPriceSection(true, line.lineSinglePriceExDiscount, line.quantity)}
                                {(line?.quantity || 0) > 1 &&
                                    renderPriceSection(false, line.lineSinglePrice, line.quantity)}
                            </>
                        )}
                    </PriceContainer>
                    {!line.description && (
                        <Counter
                            initial={line.quantity}
                            onUpdate={(quantity) => onUpdateLine(line, quantity)}
                            isDisabled={updatingBasket}
                        />
                    )}
                    {!!line?.description && (
                        <RemoveItem onClick={onEditCardClicked} variant="link">
                            {translate('checkout.giftDetails.giftEditCardLabel')}
                        </RemoveItem>
                    )}
                    <RemoveItem onClick={() => onRemoveLine(line)} variant="link">
                        {translate('productItem.productRowItem.removeProduct')}
                    </RemoveItem>
                </DesktopContent>
            </AppContentContainer>
        </BasketItemWrapper>
    );
};

const BasketItemWrapper = styled(Flex)<{ isApp?: boolean }>(({ theme, isApp }) => ({
    borderBottom: theme.general.border,
    padding: isApp ? `${theme.space[5]} 0` : `${theme.space[5]} ${theme.space[5]} ${theme.space[5]} 0`,

    '&:first-of-type': {
        borderTop: theme.general.border,
    },

    [mq('md')]: {
        flexDirection: isApp ? 'column' : 'row',
    },
}));

const LineImageWrapper = styled.div(({ theme }) => ({
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    width: '60px',
    height: '80px',
    marginRight: theme.space[5],
    padding: theme.space[2],
    backgroundColor: theme.colors.imgBg,
    borderRadius: theme.general.borderRadius,
}));

const LineTitle = styled(Text)(({ theme }) => ({
    marginBottom: theme.space[1],
    fontWeight: theme.fontWeights.bold,
    fontSize: theme.fontSizes.md,
    lineHeight: theme.lineHeights.single,
    color: theme.colors.primaryDark,
    cursor: 'pointer',

    '&:hover': {
        textDecoration: 'underline',
    },
}));

const DescriptionText = styled.div(({ theme }) => ({
    marginBottom: theme.space[2],
    fontSize: theme.fontSizes.sm,
}));

const MutedText = styled.div<{ discount?: boolean; alignRight?: boolean }>(({ theme, discount, alignRight }) => ({
    fontSize: theme.fontSizes.xs,
    color: theme.colors.primaryGrey,
    textDecoration: discount ? 'line-through' : 'initial',
    textAlign: alignRight ? 'right' : 'initial',
}));

const AlertText = styled.div(({ theme }) => ({
    fontSize: theme.fontSizes.xs,
    color: theme.colors.secondaryRed,
}));

const PriceContainer = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 0,

    [mq('md')]: {
        marginBottom: theme.space[3],
    },
}));

const AppBasketContent = styled(Flex)(({ theme }) => ({
    position: 'relative',
    marginTop: theme.space[3],
}));

const AppPriceContainer = styled.div(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    right: 0,
    top: 0,
    marginBottom: 0,
    [mq(0, 400)]: {
        position: 'relative',
        paddingTop: theme.space[2],
    },
}));

const Price = styled.div(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    fontWeight: theme.fontWeights.bold,

    [mq('md')]: {
        fontWeight: theme.fontWeights.regular,
    },
}));

const SpecialPrice = styled.div(({ theme }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    fontWeight: theme.fontWeights.bold,
    color: theme.colors.primaryTheme,

    [mq('md')]: {
        fontWeight: theme.fontWeights.regular,
    },
}));

const RemoveItem = styled(Button)(({ theme }) => ({
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.regular,
    color: theme.colors.red,
    marginTop: theme.space[2],
    justifyContent: 'flex-start',

    [mq('md')]: {
        justifyContent: 'flex-end',
        textAlign: 'right',
    },
}));

const AppCta = styled(Button)(({ theme }) => ({
    fontSize: theme.fontSizes.sm,
    fontWeight: theme.fontWeights.regular,
    color: theme.colors.grey30,
    marginTop: theme.space[2],
    justifyContent: 'flex-start',
    marginRight: theme.space[9],
}));

const MobileContent = styled(Flex)(({ theme }) => ({
    display: 'flex',
    marginTop: theme.space[3],

    [mq('md')]: {
        display: 'none',
    },
}));

const DesktopContent = styled(Flex)({
    display: 'none',

    [mq('md')]: {
        display: 'flex',
        flexShrink: 0,
    },
});

const SplashContainer = styled.div({
    position: 'absolute',
    bottom: 0,
    right: 0,
    width: '55%',
    height: 'auto',
});

const LineDescription = styled(Flex)(({ theme }) => ({
    [mq('md')]: {
        padding: `0 ${theme.space[3]} ${theme.space[3]}`,
    },
}));

const CounterContainer = styled.div(({ theme }) => ({
    marginBottom: theme.space[5],
}));
